import {
  isLargeOperation,
  type ParameterValueCreateOperation,
} from '@festina/configuration-general-types/configuration'
import {
  assertNotEmpty,
  dateUtils,
  isEmpty,
  type Optional,
} from '@festina-life/core'
import dayjs, { type Dayjs } from 'dayjs'

import {
  hasAmendOperations,
  partitionValueOperations,
} from '../parameterOperations/partitionValueOperations'
import type {
  ParameterValueEntityOperation,
  ParameterValueOperation,
} from '../parameterOperations/types'
import type { OperationWithSourceInfo } from '../types'

/**
 * An open-ended `fromDate`/`toDate` is an unbounded interval, but the interval algebra
 * below needs two comparable dates, so an absent bound stands in as one of these.
 *
 * Both must survive a `dayjs(x).format(SERVER_DATE_FORMAT)` round trip unchanged, or
 * `flattenValueOperations` stops recognising the bound it just substituted and writes it
 * back onto the operation as a real date. `dayjs` reads a year below 100 as 19xx (see
 * `dateUtils`), so year 100 is the earliest the beginning of time can be spelled.
 */
const BEGINNING_OF_TIME = '0100-01-01'
const END_OF_TIME = '9999-01-01'

/**
 * The portal cannot render the values of a parameter that has amendments: it does not
 * resolve the effective value of a create plus the deltas applied on top of it, and the
 * amend entity carries explicit dimension coordinates rather than the CSV-like
 * `dimensionsAndValues` every values helper reads. Showing the create operations alone
 * would present values that silently ignore those deltas, so each helper feeding the
 * values table yields nothing at all as soon as an amendment is among its input.
 */
export const limitValues = (
  editableOperations: OperationWithSourceInfo<ParameterValueEntityOperation>[],
  limit: Optional<number>,
): {
  operations: OperationWithSourceInfo<ParameterValueCreateOperation>[]
  hasMore: boolean
} => {
  if (hasAmendOperations(editableOperations)) {
    return { operations: [], hasMore: false }
  }

  const limitedOperations: OperationWithSourceInfo<ParameterValueCreateOperation>[] =
    []

  const totalDimensionsAndValuesCount = editableOperations.reduce(
    (acc, operation) =>
      acc +
      (operation.internalOperation.entity?.dimensionsAndValues?.length ?? 0),
    0,
  )
  const numResults = isEmpty(limit) ? Infinity : limit
  let count = 0

  for (let i = 0; i < editableOperations.length && count < numResults; i++) {
    const editableOperation = editableOperations[i]
    assertNotEmpty(editableOperation.internalOperation.entity)
    count +=
      editableOperation.internalOperation.entity.dimensionsAndValues?.length ??
      0

    if (count <= numResults) {
      limitedOperations.push(editableOperation)
    } else {
      assertNotEmpty(
        editableOperation.internalOperation.entity.dimensionsAndValues,
      )

      const exceededBy = count - numResults

      const clippedOperation = {
        ...editableOperation,
        internalOperation: {
          ...editableOperation.internalOperation,
          entity: {
            ...editableOperation.internalOperation.entity,
            dimensionsAndValues:
              editableOperation.internalOperation.entity.dimensionsAndValues.slice(
                0,
                editableOperation.internalOperation.entity.dimensionsAndValues
                  .length - exceededBy,
              ),
          },
        },
      } satisfies OperationWithSourceInfo<ParameterValueCreateOperation>
      count -= exceededBy
      limitedOperations.push(clippedOperation)
    }
  }

  return {
    operations: limitedOperations,
    hasMore: count < totalDimensionsAndValuesCount,
  }
}

type Interval = {
  from: Dayjs
  to: Dayjs
}

export const flattenValueOperations = (
  allOperations: OperationWithSourceInfo<ParameterValueEntityOperation>[],
): OperationWithSourceInfo<ParameterValueEntityOperation>[] => {
  if (hasAmendOperations(allOperations)) {
    return []
  }

  const flattenedOperations = new Map<
    string,
    OperationWithSourceInfo<ParameterValueCreateOperation>
  >()
  const claimedIntervals: Interval[] = []

  const operationsByDescendingChangeset = allOperations.toReversed()

  operationsByDescendingChangeset.forEach((editableOperation) => {
    assertNotEmpty(editableOperation.internalOperation.entity)

    const interval = {
      from: dayjs(
        editableOperation.internalOperation.entity.fromDate ??
          BEGINNING_OF_TIME,
      ),
      to: dayjs(
        editableOperation.internalOperation.entity.toDate ?? END_OF_TIME,
      ),
    } as Interval

    const acceptedIntervals = mergeInterval(interval, claimedIntervals)

    acceptedIntervals.forEach((acceptedInterval) => {
      claimedIntervals.push(acceptedInterval)

      const intervalFrom = dateUtils.dateAsServerStr(
        acceptedInterval.from.toDate(),
      )
      const intervalTo = dateUtils.dateAsServerStr(acceptedInterval.to.toDate())
      const cleanIntervalFrom =
        intervalFrom !== BEGINNING_OF_TIME ? intervalFrom : undefined
      const cleanIntervalTo =
        intervalTo !== END_OF_TIME ? intervalTo : undefined
      const operationWithNewInterval = {
        ...editableOperation,
        internalOperation: {
          ...editableOperation.internalOperation,
          entity: {
            _type: 'ParameterValueInOut',
            ...editableOperation.internalOperation.entity,
            ...(cleanIntervalFrom ? { fromDate: cleanIntervalFrom } : {}),
            ...(cleanIntervalTo ? { toDate: cleanIntervalTo } : {}),
          },
        },
      } satisfies OperationWithSourceInfo<ParameterValueCreateOperation>
      const intervalKey = `${cleanIntervalFrom}-${cleanIntervalTo}`

      flattenedOperations.set(intervalKey, operationWithNewInterval)
    })
  })

  return Array.from(flattenedOperations.values())
}

export const mergeInterval = (
  interval: Interval,
  claimedIntervals: Interval[],
): Interval[] => {
  let evaluatedIntervals = [interval]

  claimedIntervals.forEach((claimedInterval) => {
    evaluatedIntervals = evaluatedIntervals.flatMap((evaluatedInterval) =>
      restrictInterval(evaluatedInterval, claimedInterval),
    )
  })

  return evaluatedIntervals
}

export const restrictInterval = (
  interval: Interval,
  claimedInterval: Interval,
) => {
  const isIntervalFullyIncludedInClaimedInterval =
    interval.from.isBetween(
      claimedInterval.from,
      claimedInterval.to,
      'days',
      '[]',
    ) &&
    interval.to.isBetween(
      claimedInterval.from,
      claimedInterval.to,
      'days',
      '[]',
    )

  const splitIntervals: Interval[] = []

  if (!isIntervalFullyIncludedInClaimedInterval) {
    const isIntersectingOnClaimedFromDate = claimedInterval.from.isBetween(
      interval.from,
      interval.to,
      'days',
      '()',
    )
    const isIntersectingOnClaimedToDate = claimedInterval.to.isBetween(
      interval.from,
      interval.to,
      'days',
      '()',
    )

    if (isIntersectingOnClaimedToDate || isIntersectingOnClaimedFromDate) {
      if (isIntersectingOnClaimedFromDate) {
        splitIntervals.push({ from: interval.from, to: claimedInterval.from })
      }

      if (isIntersectingOnClaimedToDate) {
        splitIntervals.push({ from: claimedInterval.to, to: interval.to })
      }
    } else {
      splitIntervals.push(interval)
    }
  }

  return splitIntervals
}

export const mapLargeOperationsToResolvedValueOperations = ({
  values,
  largeOperations,
}: {
  values: OperationWithSourceInfo<ParameterValueOperation>[]
  largeOperations: ParameterValueCreateOperation[]
}): OperationWithSourceInfo<ParameterValueEntityOperation>[] =>
  values.map((value) => {
    const operation = value.internalOperation

    if (isLargeOperation(operation)) {
      const resolvedOperation = largeOperations.find(
        (parameterValueCreateOperation) =>
          parameterValueCreateOperation.operationKey ===
          operation.innerOperationKey,
      )
      assertNotEmpty(resolvedOperation)

      return {
        ...value,
        internalOperation: resolvedOperation,
      } satisfies OperationWithSourceInfo<ParameterValueCreateOperation>
    } else {
      return value
    }
  }) ?? []

export const isOperationForViewDate = ({
  operation,
  viewAt,
}: {
  operation: ParameterValueEntityOperation
  viewAt: Date
}) => {
  const intervalFrom = dayjs(operation.entity?.fromDate ?? BEGINNING_OF_TIME)
  const intervalTo = dayjs(operation.entity?.toDate ?? END_OF_TIME)
  const comparableViewAt = dayjs(viewAt)

  return comparableViewAt.isBetween(intervalFrom, intervalTo, 'days', '[)')
}

/**
 * Keeps the last operation claiming each interval, dropping the ones it shadows. Only
 * create operations are shadowed: an amendment applies a delta to the value in its
 * interval rather than replacing it, so the view constraint pipeline resolves the
 * amendments before it gets here and hands this helper the creates alone.
 */
export const removeShadowedOperations = (
  operations: OperationWithSourceInfo<ParameterValueCreateOperation>[],
) => {
  const claimedIntervals: string[] = []
  const reversedOperations = operations.toReversed()

  const finalOperations: OperationWithSourceInfo<ParameterValueCreateOperation>[] =
    []

  reversedOperations.forEach((operation) => {
    const entity = operation.internalOperation.entity
    const operationInterval = `${entity?.fromDate ?? BEGINNING_OF_TIME} - ${entity?.toDate ?? END_OF_TIME}`

    if (!claimedIntervals.includes(operationInterval)) {
      claimedIntervals.push(operationInterval)
      finalOperations.push(operation)
    }
  })

  return finalOperations.toReversed()
}

/**
 * The amend operations of the given collection that fall inside the current view window.
 */
export const getAmendOperationsForViewConstraints = ({
  allOperations,
  viewAt,
}: {
  allOperations: OperationWithSourceInfo<ParameterValueEntityOperation>[]
  viewAt?: Optional<Date>
}) =>
  partitionValueOperations(allOperations).amends.filter((amendOperation) =>
    viewAt
      ? isOperationForViewDate({
          operation: amendOperation.internalOperation,
          viewAt,
        })
      : true,
  )

export const processValueOperationsForViewConstraints = ({
  allOperations,
  isFlattened,
  viewAt,
}: {
  allOperations: OperationWithSourceInfo<ParameterValueEntityOperation>[]
  isFlattened: boolean
  viewAt?: Optional<Date>
}): OperationWithSourceInfo<ParameterValueEntityOperation>[] => {
  if (getAmendOperationsForViewConstraints({ allOperations, viewAt }).length) {
    return []
  }

  const { creates } = partitionValueOperations(allOperations)
  const nonShadowedCreateOperations = removeShadowedOperations(creates)

  return (
    isFlattened
      ? flattenValueOperations(nonShadowedCreateOperations)
      : nonShadowedCreateOperations
  ).filter((resolvedOperation) =>
    viewAt
      ? isOperationForViewDate({
          operation: resolvedOperation.internalOperation,
          viewAt,
        })
      : true,
  )
}
